import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Paper,
} from '@mui/material'
import { useDataProvider, useNotify } from 'react-admin'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'

// Interface for risk level data
interface RiskLevel {
  id?: number
  organisation_id: number
  name: string
  color: string
  lowRange: number
  highRange: number
}

interface RiskLevelEditorProps {
  open: boolean
  onClose: () => void
  onSave: () => void
  organisationId: number
  initialRiskLevelsData?: any[] // Optional prop to pass initial risk levels data
}

const RiskLevelEditor: React.FC<RiskLevelEditorProps> = ({
  open,
  onClose,
  onSave,
  organisationId,
  initialRiskLevelsData,
}) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()

  // State for risk levels
  const [riskLevels, setRiskLevels] = useState<RiskLevel[]>([])
  const [initialRiskLevels, setInitialRiskLevels] = useState<RiskLevel[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // State for distribution settings
  const [distributionType, setDistributionType] = useState<
    'gaussian' | 'linear'
  >('gaussian')
  const [mean, setMean] = useState<number>(50)
  const [stdDev, setStdDev] = useState<number>(30)

  // State for validation
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  // Heat map color palette (from Very High to Very Low)
  const HEAT_MAP_COLORS = [
    '#B71C1C', // Very High - Deep Red
    '#E53935', // High - Red
    '#FF7043', // Medium-High - Red-Orange
    '#FFA726', // Medium - Orange
    '#FFCC02', // Medium-Low - Yellow
    '#9CCC65', // Low - Yellow-Green
    '#66BB6A', // Very Low - Green
  ]

  // Default risk level names based on number of levels
  const getRiskLevelNames = (numLevels: number): string[] => {
    if (numLevels === 1) return ['Risk Level']
    if (numLevels === 2) return ['High', 'Low']
    if (numLevels === 3) return ['High', 'Medium', 'Low']
    if (numLevels === 4) return ['Very High', 'High', 'Medium', 'Low']
    if (numLevels === 5)
      return ['Very High', 'High', 'Medium', 'Low', 'Very Low']
    if (numLevels === 6)
      return ['Very High', 'High', 'Medium-High', 'Medium', 'Low', 'Very Low']
    if (numLevels === 7)
      return [
        'Very High',
        'High',
        'Medium-High',
        'Medium',
        'Medium-Low',
        'Low',
        'Very Low',
      ]
    if (numLevels === 8)
      return [
        'Extreme',
        'Very High',
        'High',
        'Medium-High',
        'Medium',
        'Medium-Low',
        'Low',
        'Very Low',
      ]
    if (numLevels === 9)
      return [
        'Extreme',
        'Very High',
        'High',
        'Medium-High',
        'Medium',
        'Medium-Low',
        'Low',
        'Very Low',
        'Minimal',
      ]
    if (numLevels === 10)
      return [
        'Extreme',
        'Critical',
        'Very High',
        'High',
        'Medium-High',
        'Medium',
        'Medium-Low',
        'Low',
        'Very Low',
        'Minimal',
      ]
    if (numLevels === 11)
      return [
        'Extreme',
        'Critical',
        'Very High',
        'High',
        'Medium-High',
        'Medium',
        'Medium-Low',
        'Low',
        'Very Low',
        'Minimal',
        'Negligible',
      ]

    // Fallback for unsupported levels
    return Array.from({ length: numLevels }, (_, i) => `Risk Level ${i + 1}`)
  }
  // Helper function to interpolate between two colors
  const interpolateColor = (
    color1: string,
    color2: string,
    factor: number
  ): string => {
    // Parse hex colors
    const hex2rgb = (hex: string) => {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return { r, g, b }
    }

    const rgb2hex = (r: number, g: number, b: number) => {
      return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g)
        .toString(16)
        .padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`
    }

    const c1 = hex2rgb(color1)
    const c2 = hex2rgb(color2)

    const r = c1.r + (c2.r - c1.r) * factor
    const g = c1.g + (c2.g - c1.g) * factor
    const b = c1.b + (c2.b - c1.b) * factor

    return rgb2hex(r, g, b)
  }

  // Fetch risk levels from the API or use provided data
  const fetchRiskLevels = useCallback(async () => {
    setLoading(true)
    setError(null) // Clear any previous errors
    try {
      let response

      // Check if initialRiskLevelsData is provided
      if (initialRiskLevelsData && initialRiskLevelsData.length > 0) {
        // Create a response object similar to what the API would return
        response = {
          data: {
            risk_levels: initialRiskLevelsData,
          },
        }
      } else {
        // Make the actual API call
        response = await dataProvider.custom(`organisation/risk-levels`, {
          method: 'GET',
        })
      }

      // Check if the response has a data property
      if (response && response.data) {
        // If data is an array, use it directly
        if (Array.isArray(response.data)) {
          setRiskLevels(response.data)
          setInitialRiskLevels(JSON.parse(JSON.stringify(response.data)))
          setIsDirty(false)
          setError(null) // Clear any error since we successfully loaded the data
        }
        // If data has a risk_levels property (from risk-assessment/summary endpoint)
        else if (
          response.data.risk_levels &&
          Array.isArray(response.data.risk_levels)
        ) {
          // Transform the risk_levels data to match the RiskLevel interface
          const transformedData = response.data.risk_levels.map((level) => {
            return {
              id: level.id,
              organisation_id: organisationId,
              name: level.level || level.name,
              color: level.color,
              lowRange: level.low_range || level.lowRange,
              highRange: level.high_range || level.highRange,
            }
          })
          setRiskLevels(transformedData)
          setInitialRiskLevels(JSON.parse(JSON.stringify(transformedData)))
          setIsDirty(false)
          setError(null) // Clear any error since we successfully loaded the data
        } else {
          console.error('Unexpected response format:', response.data)
          console.error('Response data keys:', Object.keys(response.data))
          setError('Failed to load risk levels: Unexpected data format')
        }
      } else {
        console.error('No data in response:', response)
        console.error(
          'Response keys:',
          response ? Object.keys(response) : 'response is null or undefined'
        )
        setError('Failed to load risk levels: No data received')
      }
    } catch (fetchError) {
      console.error('Error fetching risk levels:', fetchError)
      console.error(
        'Error name:',
        fetchError instanceof Error ? fetchError.name : 'Not an Error object'
      )
      console.error(
        'Error message:',
        fetchError instanceof Error ? fetchError.message : 'Unknown error'
      )
      console.error(
        'Error stack:',
        fetchError instanceof Error ? fetchError.stack : 'No stack trace'
      )
      setError(
        `Failed to load risk levels: ${
          fetchError instanceof Error ? fetchError.message : 'Unknown error'
        }`
      )
    } finally {
      setLoading(false)
    }
  }, [dataProvider, initialRiskLevelsData, organisationId])

  // Handle changes to the name field
  const handleNameChange = (riskLevel: RiskLevel, value: string) => {
    // Validate the value
    if (!value.trim()) {
      return
    }

    // Create a copy of the risk levels array
    const updatedLevels = [...riskLevels]

    // Find the index of the current risk level in the original array
    const levelIndex = updatedLevels.findIndex(
      (level) => level.id === riskLevel.id
    )
    if (levelIndex === -1) return

    // Update the current risk level
    updatedLevels[levelIndex] = {
      ...updatedLevels[levelIndex],
      name: value,
    }

    // Update the state with the new risk levels
    setRiskLevels(updatedLevels)
    setIsDirty(true)
  }

  // Handle changes to the range pickers in the list
  const handleRangeChange = (
    riskLevel: RiskLevel,
    field: 'lowRange' | 'highRange',
    value: string | number,
    index: number,
    sortedLevels: RiskLevel[]
  ) => {
    // Parse the value to a number if it's a string
    const numValue = typeof value === 'string' ? parseFloat(value) : value

    // Validate the value
    if (Number.isNaN(numValue) || numValue < 0 || numValue > 100) {
      return
    }

    // Create a copy of the risk levels array
    const updatedLevels = [...riskLevels]

    // Find the index of the current risk level in the original array
    const levelIndex = updatedLevels.findIndex(
      (level) => level.id === riskLevel.id
    )
    if (levelIndex === -1) return

    // Update the current risk level
    const updatedLevel = { ...updatedLevels[levelIndex] }

    if (field === 'lowRange') {
      // Ensure lowRange is not greater than highRange
      if (numValue >= updatedLevel.highRange) {
        return
      }

      // Store the old lowRange for reference
      const oldLowRange = updatedLevel.lowRange

      // Update the current level's lowRange
      updatedLevel.lowRange = numValue
      updatedLevels[levelIndex] = updatedLevel

      // If not the top level, adjust the highRange of the level above
      if (index > 0) {
        const aboveIndex = updatedLevels.findIndex(
          (level) => level.id === sortedLevels[index - 1].id
        )
        if (aboveIndex !== -1) {
          // Only adjust if the new value is different
          if (numValue !== oldLowRange) {
            updatedLevels[aboveIndex] = {
              ...updatedLevels[aboveIndex],
              highRange: numValue - 1,
            }
          }
        }
      }
    } else if (field === 'highRange') {
      // Ensure highRange is not less than lowRange
      if (numValue <= updatedLevel.lowRange) {
        return
      }

      // Store the old highRange for reference
      const oldHighRange = updatedLevel.highRange

      // Update the current level's highRange
      updatedLevel.highRange = numValue
      updatedLevels[levelIndex] = updatedLevel

      // If not the bottom level, adjust the lowRange of the level below
      if (index < sortedLevels.length - 1) {
        const belowIndex = updatedLevels.findIndex(
          (level) => level.id === sortedLevels[index + 1].id
        )
        if (belowIndex !== -1) {
          // Only adjust if the new value is different
          if (numValue !== oldHighRange) {
            updatedLevels[belowIndex] = {
              ...updatedLevels[belowIndex],
              lowRange: numValue + 1,
            }
          }
        }
      }
    }

    // Update the state with the new risk levels
    setRiskLevels(updatedLevels)
    setIsDirty(true)
  }

  // Save risk level changes to the API
  const saveRiskLevelChanges = async (updatedLevels: RiskLevel[]) => {
    try {
      setSaving(true)

      // Transform property names to match API requirements
      const transformedLevels = updatedLevels.map(
        ({ lowRange, highRange, ...rest }) => ({
          ...rest,
          low_range: lowRange,
          high_range: highRange,
          // Ensure name is set correctly for the API
          name: rest.name,
          // Remove level property if it exists to avoid confusion with name
          level: undefined,
        })
      )

      // Save all risk levels in a single PUT request
      await dataProvider.custom(`organisation/risk-levels`, {
        method: 'PUT',
        body: JSON.stringify(transformedLevels),
      })
    } catch (saveError) {
      console.error('Error saving risk level changes:', saveError)

      // Refresh risk levels to revert to the previous state
      fetchRiskLevels()
    } finally {
      setSaving(false)
    }
  }

  // Validate the risk level
  // Check if all ranges cover 0-100 without gaps
  const validateFullCoverage = (): boolean => {
    if (riskLevels.length === 0) return false

    // Sort risk levels by lowRange
    const sortedLevels = [...riskLevels].sort((a, b) => a.lowRange - b.lowRange)

    // Check if the lowest range starts at 0
    if (sortedLevels[0].lowRange !== 0) {
      notify('Risk levels must start at 0', { type: 'error' })
      return false
    }

    // Check if the highest range ends at 100
    if (sortedLevels[sortedLevels.length - 1].highRange !== 100) {
      notify('Risk levels must end at 100', { type: 'error' })
      return false
    }

    // Check for gaps between ranges
    for (let i = 0; i < sortedLevels.length - 1; i += 1) {
      if (sortedLevels[i].highRange + 1 !== sortedLevels[i + 1].lowRange) {
        notify('There are gaps between risk levels', { type: 'error' })
        return false
      }
    }

    return true
  }

  // Helper function to redistribute risk level ranges using Gaussian and Linear distributions
  const redistributeRanges = (
    levels: RiskLevel[],
    distributionTypeOverride?: 'gaussian' | 'linear'
  ): RiskLevel[] => {
    // Use component-defined defaults if parameters are not overridden
    const currentDistributionType = distributionTypeOverride ?? distributionType
    const currentMean = mean
    const currentStdDev = stdDev

    if (levels.length === 0) return []

    const sortedLevels = [...levels].sort((a, b) => a.lowRange - b.lowRange)
    const numLevels = sortedLevels.length

    // Gaussian probability density function (PDF)
    const gaussianPDF = (x: number): number => {
      const exponent = -((x - currentMean) ** 2) / (2 * currentStdDev ** 2)
      return (1 / (currentStdDev * Math.sqrt(2 * Math.PI))) * Math.exp(exponent)
    }

    // Numerical integration using the Trapezoidal Rule
    const integrateArea = (
      start: number,
      end: number,
      steps: number = 1000
    ): number => {
      const width = (end - start) / steps
      let area = 0

      for (let i = 0; i < steps; i += 1) {
        const x1 = start + i * width
        const x2 = x1 + width

        const y1 = gaussianPDF(x1)
        const y2 = gaussianPDF(x2)

        area += ((y1 + y2) / 2) * width // Trapezoid area
      }

      return area
    }

    const boundaries: number[] = [0] // Start with 0 boundary
    const areas: number[] = [] // Stores the area percentage for Gaussian distribution
    let finalLevels: any[]
    if (currentDistributionType === 'gaussian') {
      // 1. Divide the x-axis (0–100) into equal slices
      const sliceWidth = 100 / numLevels

      for (let i = 0; i < numLevels; i += 1) {
        const start = i * sliceWidth
        const end = (i + 1) * sliceWidth

        // Calculate area for each slice under the Gaussian curve
        const area = integrateArea(start, end)
        areas.push(area)
      }

      // 2. Normalize the total area
      const totalArea = areas.reduce((sum, a) => sum + a, 0)

      // Convert slice areas into percentage ranges of the total area
      const percentageRanges = areas.map((area) => (area / totalArea) * 100)

      // 3. Map boundaries while ensuring the edges are bounded by [0, 100]
      let accumulatedPercentage = 0
      finalLevels = sortedLevels.map((level, index) => {
        const lowRange = Math.max(0, Math.round(accumulatedPercentage)) // Clamp to 0
        accumulatedPercentage += percentageRanges[index]

        // If this is the last level, set highRange to 100
        const highRange =
          index === numLevels - 1
            ? 100
            : Math.min(100, Math.round(accumulatedPercentage - 1)) // Clamp to 100

        return {
          ...level,
          lowRange,
          highRange,
        }
      })
    } else {
      // Linear Distribution
      const rangeSize = 100 / numLevels

      for (let i = 1; i < numLevels; i += 1) {
        boundaries.push(Math.round(i * rangeSize))
      }

      // Ensure the last boundary is at 100
      boundaries.push(100)

      // Map equally spaced boundaries back to levels
      finalLevels = sortedLevels.map((level, index) => ({
        ...level,
        lowRange: boundaries[index],
        highRange: boundaries[index + 1] - (index < numLevels - 1 ? 1 : 0),
      }))
    }

    return finalLevels
  }

  // Updated function to generate a color based on position using heat map palette
  const generateColor = (position: number): string => {
    // Clamp position between 0 and 1
    position = Math.max(0, Math.min(1, position))

    // If we have exact positions that match our palette
    if (HEAT_MAP_COLORS.length > 0) {
      const segmentSize = 1 / (HEAT_MAP_COLORS.length - 1)
      const segmentIndex = Math.floor(position / segmentSize)

      // If we're at the exact end
      if (segmentIndex >= HEAT_MAP_COLORS.length - 1) {
        return HEAT_MAP_COLORS[HEAT_MAP_COLORS.length - 1]
      }

      // If we're at an exact segment boundary
      if (position % segmentSize === 0) {
        return HEAT_MAP_COLORS[segmentIndex]
      }

      // Interpolate between two colors
      const localPosition = (position % segmentSize) / segmentSize
      return interpolateColor(
        HEAT_MAP_COLORS[segmentIndex],
        HEAT_MAP_COLORS[segmentIndex + 1],
        localPosition
      )
    }

    // Fallback to original red-yellow-green generation if no palette
    let r
    let g
    const b = 0

    if (position < 0.5) {
      // Red to Yellow (0 to 0.5)
      r = 255
      g = Math.round(255 * (position * 2))
    } else {
      // Yellow to Green (0.5 to 1)
      r = Math.round(255 * (1 - (position - 0.5) * 2))
      g = 255
    }

    return `#${r.toString(16).padStart(2, '0')}${g
      .toString(16)
      .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  }

  // Updated function to update colors and names based on position in the range
  const updateColors = (levels: RiskLevel[]): RiskLevel[] => {
    if (levels.length === 0) return []

    // Create a map to store the original order of levels by ID
    const levelMap = new Map<number | undefined, number>()
    levels.forEach((level, index) => {
      levelMap.set(level.id, index)
    })

    // Sort levels by lowRange (highest risk first, lowest score)
    const sortedLevels = [...levels].sort((a, b) => a.lowRange - b.lowRange)

    // Get default names for this number of levels
    const defaultNames = getRiskLevelNames(sortedLevels.length)

    // Update colors and names based on position
    const colorUpdatedLevels = sortedLevels.map((level, index) => {
      // Calculate position (0 to 1) based on index
      const position =
        sortedLevels.length > 1 ? index / (sortedLevels.length - 1) : 0

      // Always use the default name to ensure names are updated dynamically
      return {
        ...level,
        color: generateColor(position),
        name: defaultNames[index],
      }
    })

    // Restore the original order of levels
    return colorUpdatedLevels.sort((a, b) => {
      const indexA = levelMap.get(a.id) ?? 0
      const indexB = levelMap.get(b.id) ?? 0
      return indexA - indexB
    })
  }

  // Delete a risk level
  const handleDelete = (riskLevel: RiskLevel) => {
    // Find the index of the level to be deleted
    const deleteIndex = riskLevels.findIndex(
      (level) => level.id === riskLevel.id
    )
    if (deleteIndex === -1) return

    // Create a copy of the risk levels array
    const updatedLevels = [...riskLevels]

    // Find the levels above and below the deleted level
    const aboveLevel = deleteIndex > 0 ? updatedLevels[deleteIndex - 1] : null
    const belowLevel =
      deleteIndex < updatedLevels.length - 1
        ? updatedLevels[deleteIndex + 1]
        : null

    // Remove the level to be deleted
    updatedLevels.splice(deleteIndex, 1)

    if (updatedLevels.length > 0) {
      // If the deleted level was the top level (lowRange = 0), set the next level's lowRange to 0
      if (riskLevel.lowRange === 0 && belowLevel) {
        const belowIndex = updatedLevels.findIndex(
          (level) => level.id === belowLevel.id
        )
        if (belowIndex !== -1) {
          updatedLevels[belowIndex] = {
            ...updatedLevels[belowIndex],
            lowRange: 0,
          }
        }
      }

      // If the deleted level was the bottom level (highRange = 100), set the previous level's highRange to 100
      if (riskLevel.highRange === 100 && aboveLevel) {
        const aboveIndex = updatedLevels.findIndex(
          (level) => level.id === aboveLevel.id
        )
        if (aboveIndex !== -1) {
          updatedLevels[aboveIndex] = {
            ...updatedLevels[aboveIndex],
            highRange: 100,
          }
        }
      }

      // If the deleted level was in the middle, adjust the ranges of the adjacent levels
      if (aboveLevel && belowLevel) {
        const aboveIndex = updatedLevels.findIndex(
          (level) => level.id === aboveLevel.id
        )
        const belowIndex = updatedLevels.findIndex(
          (level) => level.id === belowLevel.id
        )

        if (aboveIndex !== -1 && belowIndex !== -1) {
          // Calculate the middle point between the adjacent levels
          const middlePoint = Math.floor(
            (aboveLevel.lowRange + belowLevel.highRange) / 2
          )

          // Adjust the ranges of the adjacent levels
          updatedLevels[aboveIndex] = {
            ...updatedLevels[aboveIndex],
            highRange: middlePoint - 1,
          }

          updatedLevels[belowIndex] = {
            ...updatedLevels[belowIndex],
            lowRange: middlePoint,
          }
        }
      }

      // Redistribute ranges evenly
      const redistributedLevels = redistributeRanges(updatedLevels)

      // Update colors
      const colorUpdatedLevels = updateColors(redistributedLevels)

      // Update the state with the new risk levels
      setRiskLevels(colorUpdatedLevels)
      setIsDirty(true)
    }
  }

  // Handle close with confirmation if dirty
  const handleClose = () => {
    if (isDirty) {
      setShowConfirmDialog(true)
    } else {
      onClose()
    }
  }

  // Save all changes and close the editor
  const handleSaveAll = async () => {
    if (!validateFullCoverage()) {
      return
    }

    try {
      setSaving(true)

      // Save all risk levels to the API
      await saveRiskLevelChanges(riskLevels)

      // Reset the dirty state
      setIsDirty(false)

      onSave()
      onClose()
    } catch (saveError) {
      console.error('Error saving risk levels:', saveError)
    } finally {
      setSaving(false)
    }
  }

  // Updated handleAddBetween function with better default naming
  const handleAddBetween = (beforeLevel: RiskLevel, afterLevel: RiskLevel) => {
    // Calculate the position for color (0 to 1)
    const beforeIndex = riskLevels.findIndex(
      (level) => level.id === beforeLevel.id
    )
    const afterIndex = riskLevels.findIndex(
      (level) => level.id === afterLevel.id
    )

    // Ensure afterIndex is the next index after beforeIndex
    if (afterIndex !== beforeIndex + 1) {
      console.error('Levels are not adjacent:', beforeIndex, afterIndex)
    }

    // Calculate the middle of the range between the two levels
    const middleLowRange = beforeLevel.highRange + 1
    const middleHighRange = afterLevel.lowRange - 1

    // Create the new risk level with a temporary ID
    const tempId = Date.now() // Use timestamp as temporary ID
    const newLevel: RiskLevel = {
      id: tempId,
      organisation_id: organisationId,
      name: 'New Level', // This will be updated by updateColors
      color: '#FFA726', // Temporary color, will be updated
      lowRange: middleLowRange,
      highRange: middleHighRange,
    }

    // Create a copy of the risk levels array
    const updatedLevels = [...riskLevels]

    // Insert the new level at the position after beforeIndex
    updatedLevels.splice(beforeIndex + 1, 0, newLevel)

    // Redistribute ranges evenly
    const redistributedLevels = redistributeRanges(updatedLevels)

    // Update colors and names
    const colorUpdatedLevels = updateColors(redistributedLevels)

    // Update the state with the new risk levels
    setRiskLevels(colorUpdatedLevels)
    setIsDirty(true)
  }

  // Function to regenerate all names and colors (useful for reset functionality)
  const regenerateNamesAndColors = (levels: RiskLevel[]): RiskLevel[] => {
    if (levels.length === 0) return []

    // Sort by lowRange
    const sortedLevels = [...levels].sort((a, b) => a.lowRange - b.lowRange)
    const defaultNames = getRiskLevelNames(sortedLevels.length)

    // Create a map to restore original order
    const levelMap = new Map<number | undefined, number>()
    levels.forEach((level, index) => {
      levelMap.set(level.id, index)
    })

    // Update with new names and colors
    const updatedLevels = sortedLevels.map((level, index) => {
      const position =
        sortedLevels.length > 1 ? index / (sortedLevels.length - 1) : 0

      return {
        ...level,
        name: defaultNames[index],
        color: generateColor(position),
      }
    })

    // Restore original order
    return updatedLevels.sort((a, b) => {
      const indexA = levelMap.get(a.id) ?? 0
      const indexB = levelMap.get(b.id) ?? 0
      return indexA - indexB
    })
  }

  // Fetch risk levels when the component mounts
  useEffect(() => {
    if (open) {
      fetchRiskLevels()
    }
  }, [open, fetchRiskLevels])

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="risk-level-editor-title"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          id="risk-level-editor-title"
          sx={{
            borderBottom: '1px solid #e5e7eb',
            px: 4,
            py: 3,
            backgroundColor: 'white',
          }}
        >
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            Risk Level Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Configure risk levels and their compliance score ranges
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Box>
              {/* Distribution controls */}
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  mb: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(to right, #ebf5ff, #f0f7ff)',
                  border: '1px solid #dbeafe',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: '#1e3a8a',
                  }}
                >
                  Distribution Settings
                </Typography>

                <FormControl component="fieldset" sx={{ mb: 1, width: '100%' }}>
                  <FormLabel
                    component="legend"
                    sx={{
                      mb: 0.5,
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      color: '#4b5563',
                    }}
                  >
                    Distribution Type
                  </FormLabel>
                  <RadioGroup
                    row
                    value={distributionType}
                    onChange={(e) => {
                      const newDistributionType = e.target.value as
                        | 'gaussian'
                        | 'linear'
                      setDistributionType(newDistributionType)
                      setIsDirty(true)

                      // Use the existing redistributeRanges function instead of inline logic
                      // Pass the new distribution type directly to ensure it uses the new value
                      const redistributedLevels = redistributeRanges(
                        riskLevels,
                        newDistributionType
                      )

                      setRiskLevels(redistributedLevels)
                    }}
                  >
                    <FormControlLabel
                      value="gaussian"
                      control={
                        <Radio
                          sx={{
                            color: '#3b82f6',
                            '&.Mui-checked': {
                              color: '#3b82f6',
                            },
                          }}
                        />
                      }
                      label="Standard (Gaussian)"
                      sx={{
                        mr: 4,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          color: '#4b5563',
                        },
                      }}
                    />
                    <FormControlLabel
                      value="linear"
                      control={
                        <Radio
                          sx={{
                            color: '#3b82f6',
                            '&.Mui-checked': {
                              color: '#3b82f6',
                            },
                          }}
                        />
                      }
                      label="Linear"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          color: '#4b5563',
                        },
                      }}
                    />
                  </RadioGroup>
                </FormControl>

                {/* Mean and standard deviation sliders temporarily hidden */}
              </Paper>

              {/* List of risk levels */}
              <Box sx={{ mb: 3, mt: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: '#111827', pl: '10px' }}
                  >
                    Risk Levels
                  </Typography>
                </Box>

                {riskLevels.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      color: '#6b7280',
                      bgcolor: '#f9fafb',
                      borderRadius: '8px',
                    }}
                  >
                    No risk levels defined.
                  </Typography>
                ) : (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}
                  >
                    {riskLevels
                      .sort((a, b) => a.lowRange - b.lowRange)
                      .map((riskLevel, index, sortedLevels) => (
                        <React.Fragment key={riskLevel.id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1,
                              mt: 0,
                              borderRadius: '12px',
                              border: '1px solid #e5e7eb',
                              transition: 'box-shadow 0.2s, border-color 0.2s',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                borderColor: '#d1d5db',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    bgcolor: riskLevel.color,
                                    flexShrink: 0,
                                    border: '2px solid white',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  }}
                                />
                                <TextField
                                  size="small"
                                  value={riskLevel.name}
                                  onChange={(e) =>
                                    handleNameChange(riskLevel, e.target.value)
                                  }
                                  variant="outlined"
                                  sx={{
                                    width: '160px',
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      fontSize: '1rem',
                                      fontWeight: 500,
                                    },
                                  }}
                                />
                              </Box>

                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'top',
                                    gap: 1,
                                  }}
                                >
                                  <TextField
                                    label="Min"
                                    type="number"
                                    size="small"
                                    value={riskLevel.lowRange}
                                    disabled={index === 0} // Disable for top level
                                    onChange={(e) =>
                                      handleRangeChange(
                                        riskLevel,
                                        'lowRange',
                                        e.target.value,
                                        index,
                                        sortedLevels
                                      )
                                    }
                                    InputProps={{
                                      inputProps: { min: 0, max: 100 },
                                    }}
                                    InputLabelProps={{
                                      shrink: true,
                                      style: { top: 0 },
                                    }}
                                    sx={{
                                      width: '80px',
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                      },
                                    }}
                                  />
                                  <TextField
                                    label="Max"
                                    type="number"
                                    size="small"
                                    value={riskLevel.highRange}
                                    disabled={index === sortedLevels.length - 1} // Disable for bottom level
                                    onChange={(e) =>
                                      handleRangeChange(
                                        riskLevel,
                                        'highRange',
                                        e.target.value,
                                        index,
                                        sortedLevels
                                      )
                                    }
                                    InputProps={{
                                      inputProps: { min: 0, max: 100 },
                                    }}
                                    InputLabelProps={{
                                      shrink: true,
                                      style: { top: 0 },
                                    }}
                                    sx={{
                                      width: '80px',
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                      },
                                    }}
                                  />
                                </Box>

                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <input
                                    type="color"
                                    value={riskLevel.color}
                                    onChange={(e) => {
                                      const updatedLevels = [...riskLevels]
                                      const levelIndex =
                                        updatedLevels.findIndex(
                                          (level) => level.id === riskLevel.id
                                        )
                                      if (levelIndex !== -1) {
                                        updatedLevels[levelIndex] = {
                                          ...updatedLevels[levelIndex],
                                          color: e.target.value,
                                        }
                                        setRiskLevels(updatedLevels)
                                        setIsDirty(true)
                                      }
                                    }}
                                    style={{
                                      width: '36px',
                                      height: '36px',
                                      padding: '0',
                                      border: 'none',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    }}
                                  />

                                  <IconButton
                                    aria-label="delete"
                                    onClick={() => handleDelete(riskLevel)}
                                    disabled={saving}
                                    sx={{
                                      ml: 1,
                                      color: '#ef4444',
                                      '&:hover': {
                                        bgcolor: '#fee2e2',
                                      },
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Box>
                          </Paper>

                          {/* Add "+" icon between levels */}
                          {index < sortedLevels.length - 1 && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                height: 0,
                                overflow: 'visible',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleAddBetween(
                                    riskLevel,
                                    sortedLevels[index + 1]
                                  )
                                }
                                disabled={saving}
                                sx={{
                                  position: 'relative',
                                  top: '-14px',
                                  backgroundColor: '#3b82f6',
                                  color: 'white',
                                  width: '28px',
                                  height: '28px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  '&:hover': {
                                    backgroundColor: '#2563eb',
                                  },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </React.Fragment>
                      ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button
            onClick={handleClose}
            disabled={saving}
            sx={{
              color: '#4b5563',
              bgcolor: '#f3f4f6',
              borderRadius: '8px',
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#e5e7eb',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAll}
            variant="contained"
            disabled={saving || loading || !isDirty}
            startIcon={<CheckIcon />}
            sx={{
              bgcolor: '#3b82f6',
              borderRadius: '8px',
              px: 2,
              py: 0.5,
              ml: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#2563eb',
              },
              '&.Mui-disabled': {
                bgcolor: '#bfdbfe',
                color: '#ffffff',
              },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        aria-labelledby="confirm-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            maxWidth: '450px',
          },
        }}
      >
        <DialogTitle
          id="confirm-dialog-title"
          sx={{
            px: 4,
            py: 3,
            backgroundColor: 'white',
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ fontWeight: 'bold', color: '#111827' }}
          >
            Unsaved Changes
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 2 }}>
          <Typography sx={{ color: '#4b5563' }}>
            You have unsaved changes. Do you want to save them before closing?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button
            onClick={() => {
              setShowConfirmDialog(false)
              onClose()
            }}
            sx={{
              color: '#ef4444',
              bgcolor: '#fee2e2',
              borderRadius: '8px',
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#fecaca',
              },
            }}
          >
            No
          </Button>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            sx={{
              color: '#4b5563',
              bgcolor: '#f3f4f6',
              borderRadius: '8px',
              px: 2,
              py: 0.5,
              ml: 1,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#e5e7eb',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setShowConfirmDialog(false)
              await handleSaveAll()
            }}
            variant="contained"
            sx={{
              bgcolor: '#3b82f6',
              borderRadius: '8px',
              px: 2,
              py: 0.5,
              ml: 1,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#2563eb',
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RiskLevelEditor
